if (!customElements.get("quick-view-modal")) {
  customElements.define(
    "quick-view-modal",
    class QuickViewModal extends ModalDialog {
      savedMainProductModals = [];

      constructor() {
        super();
        this.modalContent = this.querySelector('[id^="QuickViewInfo-"]');
        if (!this.modalContent) return;
      }

      cleanup() {
        this.sliderObserver?.disconnect();
        this.subSliderObserver?.disconnect();
        this.stackedPreviewCleanup?.();
        this.stackedPreviewCleanup = null;

        if (this.modalContent.querySelector(".swiper")) {
          this.modalContent.querySelectorAll(".swiper").forEach((el) => {
            el.swiper?.destroy(true, true);
          });
        }
      }

      removeModals() {
        // * Clean all modal-dialogs to avoid miss opening and redundant additions
        document
          .querySelectorAll(
            'modal-dialog[id^="ProductModal-variant_picker"], modal-dialog[id^="ProductModal-product_buy_buttons"]'
          )
          .forEach((dialog) => dialog.remove());
      }

      saveMainProductModals() {
        const modals = document.querySelectorAll(
          'modal-dialog[id^="ProductModal-variant_picker"], modal-dialog[id^="ProductModal-product_buy_buttons"]'
        );

        modals.forEach((modal) => {
          this.savedMainProductModals.push({
            element: modal,
            parent: modal.parentNode,
          });
        });
      }

      restoreMainProductModals() {
        this.savedMainProductModals.forEach(({ element, parent }) => {
          parent.appendChild(element);
        });

        this.savedMainProductModals = [];
      }

      autoPlayingSwipers = [];
      autoPlayingVideos = [];

      pauseBackgroundAutoplay() {
        document.querySelectorAll(".swiper").forEach((swiperEl) => {
          const swiper = swiperEl.swiper;
          if (
            !swiper ||
            swiper.destroyed ||
            swiperEl.closest("quick-view-modal") ||
            typeof swiper.autoplay?.stop !== "function" ||
            !swiper.params?.autoplay
          )
            return;

          swiper.autoplay.stop();
          this.autoPlayingSwipers.push(swiper);
        });
      }

      pauseBackgroundVideos() {
        document.querySelectorAll("video").forEach((video) => {
          if (
            video.closest("quick-view-modal") ||
            video.paused ||
            video.ended
          ) {
            return;
          }

          this.autoPlayingVideos.push(video);
          video.pause();
        });
      }

      resumeBackgroundAutoplay() {
        this.autoPlayingSwipers.forEach((swiper) => {
          if (swiper && !swiper.destroyed && swiper.el?.isConnected) {
            swiper.autoplay.start();
          }
        });

        this.autoPlayingSwipers = [];
      }

      resumeBackgroundVideos() {
        this.autoPlayingVideos.forEach((video) => {
          if (!video || !video.isConnected) return;

          video.play().catch(() => {});
        });
        this.autoPlayingVideos = [];
      }

      hide(preventFocus = false) {
        this.modalContent.innerHTML = "";

        if (preventFocus) this.openedBy = null;

        super.hide();

        document.body.dispatchEvent(new CustomEvent("quick-view-closed"));
        this.cleanup();
        this.removeModals();
        this.restoreMainProductModals();
        this.resumeBackgroundAutoplay();
        this.resumeBackgroundVideos();
      }

      show(opener) {
        if (!opener || !opener?.dataset?.productUrl) return;

        this.saveMainProductModals();
        this.removeModals();
        this.pauseBackgroundVideos();

        opener.setAttribute("aria-disabled", true);
        opener.classList.add("loading");
        const btnSpinner = opener.querySelector(".loading-overlay__spinner");
        if (btnSpinner) btnSpinner.classList.remove("hidden");

        fetch(opener.dataset.productUrl)
          .then((response) => response.text())
          .then((responseText) => {
            const responseHTML = new DOMParser().parseFromString(
              responseText,
              "text/html"
            );
            this.productElement = responseHTML.querySelector(
              '[id^="MainProduct-"]'
            );
            if (!this.productElement) return;

            this.prepareProductHTML();
            this.setModalHTML(opener);
            this.initSubSlider();
            this.initSlider();
            this.initPagination();
            this.initStackedPreviews();
            this.initShopifyElements();
            super.show(opener);
            this.setFocusElement(opener);
            this.pauseBackgroundAutoplay();
          })
          .finally(() => {
            opener.removeAttribute("aria-disabled");
            opener.classList.remove("loading");
            if (btnSpinner) btnSpinner.classList.add("hidden");
          });
      }

      prepareProductHTML() {
        this.preventDuplicatedIDs();
        this.removeDOMElements();
      }

      removeDOMElements() {
        const selectors = [
          ".product__breadcrumbs",
          ".product__tags",
          ".product-sticky-add-bar",
          ".pickup-availability",
          ".avaliability-notification",
          "avaliability-notification-drawer",
          ".complementary-products",
          ".product__info-tabs",
          ".countdown",
          ".product__media-toggle",
          ".product__media-mobile-icon",
          ".product__media-cursor",
          ".product__media-info",
          "[data-avaliability-notification-script]",
          ".custom-liquid",
          ".product-accordion",
          ".product-app",
        ];
        const removingElements = this.productElement.querySelectorAll(
          selectors.join(", ")
        );
        removingElements.forEach((el) => el.remove());
      }

      preventDuplicatedIDs() {
        const sectionId = this.productElement.dataset.section;
        this.productElement.innerHTML =
          this.productElement.innerHTML.replaceAll(
            sectionId,
            `quickview-${sectionId}`
          );
        this.productElement
          .querySelectorAll("variant-selects, variant-radios")
          .forEach((variantSelect) => {
            variantSelect.dataset.originalSection = sectionId;
          });
      }

      setModalHTML(opener) {
        this.modalContent.innerHTML = this.productElement.innerHTML;

        this.reinjectScriptTags();
        this.createFullDetailsButton(opener);
        this.preventScrollBody();
        this.preventVariantURLSwitching();
        this.clearMediaClasses();
        this.reorderBlocks();
        if (this.classList.contains("quick-view-modal--drawer")) {
          this.updateImageSizesDrawer();
        }
      }

      reinjectScriptTags() {
        // Reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
        this.modalContent.querySelectorAll("script").forEach((oldScriptTag) => {
          const newScriptTag = document.createElement("script");
          Array.from(oldScriptTag.attributes).forEach((attribute) => {
            newScriptTag.setAttribute(attribute.name, attribute.value);
          });
          newScriptTag.appendChild(
            document.createTextNode(oldScriptTag.innerHTML)
          );
          oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
        });
      }

      preventVariantURLSwitching() {
        const variantPickerEl = this.modalContent.querySelector(
          "variant-radios,variant-selects"
        );
        if (variantPickerEl) {
          variantPickerEl.setAttribute("data-update-url", "false");
        }
      }

      preventScrollBody() {
        this.modalContent
          .querySelectorAll(".product-form__controls-group input[type=radio]")
          .forEach((input) => {
            input.addEventListener("focus", (e) => {
              const scrollTop =
                document.body.scrollTop || document.documentElement.scrollTop;
              setTimeout(() => {
                window.scrollTo(0, scrollTop);
              }, 0);
            });
          });
      }

      initShopifyElements() {
        if (window.Shopify && Shopify.PaymentButton) {
          Shopify.PaymentButton.init();
        }

        if (window.ProductModel) {
          window.ProductModel.loadShopifyXR();
        }
      }

      initSubSlider = () => {
        const sliderEl = this.modalContent.querySelector(".js-media-sublist");
        if (!sliderEl) return;

        const desktopType = sliderEl.dataset?.desktopType || "slider";
        const mobileType = sliderEl.dataset?.mobileType || "slider";
        const hasDesktopSwiper = desktopType === "slider_previews";
        const hasMobileSwiper = mobileType === "slider_previews";

        const mediaList = this.modalContent.querySelector(
          ".product__media-list"
        );
        const pagination =
          this.modalContent.querySelector(".swiper-pagination");

        if (mediaList && pagination) {
          const correspondingHeight = mediaList.offsetHeight;

          sliderEl.parentNode.style.height = correspondingHeight + "px";
          pagination.style.height = correspondingHeight + "px";
        }

        const slidesCount = this.modalContent.querySelectorAll(
          ".product__media-subitem"
        ).length;

        const syncThumbsTouchState = (swiper) => {
          if (!swiper || swiper.destroyed) return;

          if (swiper.isHorizontal()) {
            swiper.params.allowTouchMove = true;
            swiper.allowTouchMove = true;
            return;
          }

          const slides = Array.from(swiper.slides || []);
          if (!slides.length) return;

          const firstSlide = slides[0];
          const lastSlide = slides[slides.length - 1];
          const contentHeight =
            lastSlide.offsetTop + lastSlide.offsetHeight - firstSlide.offsetTop;
          const hasOverflow = contentHeight > swiper.el.clientHeight + 1;

          swiper.params.allowTouchMove = hasOverflow;
          swiper.allowTouchMove = hasOverflow;

          if (!hasOverflow && swiper.translate !== 0) {
            swiper.setTranslate(0);
            swiper.updateProgress(0);
          }
        };

        const initSwiper = () => {
          const thumbSlider = new Swiper(sliderEl, {
            spaceBetween: 2,
            slidesPerView: Math.min(4, slidesCount),
            loop: false,
            direction: "horizontal",
            allowTouchMove: true,
            watchSlidesProgress: true,
            watchOverflow: true,
            observer: true,
            observeParents: true,
            autoHeight: false,
            slideToClickedSlide: true,
            on: {
              afterInit: syncThumbsTouchState,
              breakpoint: syncThumbsTouchState,
              resize: syncThumbsTouchState,
              observerUpdate: syncThumbsTouchState,
              touchEnd: function (swiper) {
                syncThumbsTouchState(swiper);

                let range = 5;
                let diff = (swiper.touches.diff = swiper.isHorizontal()
                  ? swiper.touches.currentX - swiper.touches.startX
                  : swiper.touches.currentY - swiper.touches.startY);
                if (Math.abs(diff) < range) swiper.allowClick = true;
              },

              click: function (swiper) {
                if (
                  window.innerWidth < 990 ||
                  !hasDesktopSwiper ||
                  !swiper.clickedSlide ||
                  !swiper.allowClick
                )
                  return;

                const clickedSlide = swiper.clickedSlide;
                const realIndex = parseInt(
                  clickedSlide.dataset.swiperSlideIndex ?? swiper.clickedIndex
                );

                const translateY = swiper.translate;
                const slideTop = clickedSlide.offsetTop;
                const slideBottom = slideTop + clickedSlide.offsetHeight;
                const visibleTop = -translateY;
                const visibleBottom = visibleTop + swiper.height;

                if (slideTop >= visibleTop && slideBottom <= visibleBottom) {
                  if (swiper.thumbs && swiper.thumbs.swiper) {
                    swiper.thumbs.swiper.slideTo(realIndex, 0, false);
                  }
                  swiper.activeIndex = realIndex;
                  swiper.updateSlidesClasses();
                  swiper.emit("slideChange");
                } else {
                  swiper.slideToLoop(realIndex);
                }
              },
            },
            breakpoints: {
              576: {
                slidesPerView: Math.min(6, slidesCount),
              },
              990: {
                slidesPerView: "auto",
                direction: "vertical",
              },
            },
          });

          return thumbSlider;
        };

        if (hasDesktopSwiper || hasMobileSwiper) {
          initSwiper();

          this.subSliderObserver = new ResizeObserver((entries) => {
            entries.forEach((entry) => {
              if (sliderEl.swiper) {
                if (window.innerWidth >= 990 && hasDesktopSwiper) {
                  sliderEl.swiper.enable();
                  sliderEl.swiper.changeDirection("vertical", true);
                  syncThumbsTouchState(sliderEl.swiper);
                }

                if (
                  window.innerWidth >= 990 &&
                  desktopType === "stacked_previews"
                ) {
                  sliderEl.swiper.disable();
                }

                if (window.innerWidth < 990 && hasMobileSwiper) {
                  sliderEl.swiper.enable();
                  sliderEl.swiper.changeDirection("horizontal", true);
                  syncThumbsTouchState(sliderEl.swiper);
                }
              }
            });
          });

          this.subSliderObserver.observe(this.modalContent);
        }
      };

      initPagination = () => {
        const fill = this.modalContent.querySelector(
          ".swiper-pagination-progressbar-fill"
        );

        if (!fill) return;

        const slides = [
          ...this.modalContent.querySelectorAll(".product__media-item"),
        ];

        const observer = new IntersectionObserver(
          (entries) => {
            const visibleSlides = entries
              .filter((e) => e.isIntersecting)
              .map((e) => slides.indexOf(e.target));

            if (!visibleSlides.length) return;

            const current = Math.max(...visibleSlides);
            const progress = (current + 1) / slides.length;

            fill.style.transform = `scaleY(${progress})`;
          },
          { threshold: 0.6 }
        );

        slides.forEach((slide) => observer.observe(slide));
      };

      initStackedPreviews = () => {
        this.stackedPreviewCleanup?.();
        this.stackedPreviewCleanup = null;

        const mediaList = this.modalContent.querySelector(
          ".product__media-list[data-desktop-type='stacked_previews']"
        );
        const thumbs = this.modalContent.querySelector(
          ".product__media-sublist[data-desktop-type='stacked_previews']"
        );
        if (!mediaList || !thumbs) return;

        const mediaEls = Array.from(
          mediaList.querySelectorAll("[data-media-id]")
        );
        const thumbEls = Array.from(
          thumbs.querySelectorAll("[data-media-sub-id]")
        );
        if (!mediaEls.length || !thumbEls.length) return;

        const setActiveThumb = (mediaId) => {
          thumbEls.forEach((thumb) => {
            thumb.classList.toggle(
              "active",
              thumb.dataset.mediaSubId === mediaId
            );
          });

          const activeThumb = thumbs.querySelector(
            `[data-media-sub-id="${mediaId}"]`
          );
          if (!activeThumb) return;

          const thumbHeight = activeThumb.offsetHeight;
          const activeIndex = thumbEls.findIndex(
            (el) => el.dataset.mediaSubId === mediaId
          );
          const nextIndex = Math.min(activeIndex + 1, thumbEls.length - 1);
          const scrollTarget =
            thumbEls[nextIndex].offsetTop -
            thumbs.clientHeight / 2 +
            thumbHeight;

          thumbs.scrollTo({
            top: scrollTarget,
            behavior: "smooth",
          });
        };

        const scrollToMedia = (mediaId) => {
          const targetMedia = mediaList.querySelector(
            `[data-media-id="${mediaId}"]`
          );
          if (!targetMedia) return;

          const offset =
            targetMedia.getBoundingClientRect().top -
            this.modalContent.getBoundingClientRect().top +
            this.modalContent.scrollTop;

          this.modalContent.scrollTo({
            top: offset,
            behavior: "smooth",
          });
          setActiveThumb(mediaId);
        };

        const onScroll = () => {
          if (window.innerWidth < 990) return;

          const containerRect = this.modalContent.getBoundingClientRect();
          const viewportMiddle =
            containerRect.top + this.modalContent.clientHeight / 2;

          let closestMedia = null;
          let closestDistance = Infinity;

          for (const mediaEl of mediaEls) {
            const rect = mediaEl.getBoundingClientRect();
            const distance = Math.abs(rect.top - viewportMiddle);

            if (rect.top < viewportMiddle && distance < closestDistance) {
              closestDistance = distance;
              closestMedia = mediaEl;
            }
          }

          if (closestMedia) {
            setActiveThumb(closestMedia.dataset.mediaId);
          }
        };

        requestAnimationFrame(() => {
          onScroll();
        });

        this.modalContent.addEventListener("scroll", onScroll, {
          passive: true,
        });

        const thumbClickCleanups = [];

        thumbEls.forEach((thumb) => {
          const onThumbClick = () => {
            if (window.innerWidth < 990) return;
            scrollToMedia(thumb.dataset.mediaSubId);
          };

          thumb.addEventListener("click", onThumbClick);
          thumbClickCleanups.push(() => {
            thumb.removeEventListener("click", onThumbClick);
          });
        });

        this.stackedPreviewCleanup = () => {
          this.modalContent.removeEventListener("scroll", onScroll);
          thumbClickCleanups.forEach((cleanup) => cleanup());
        };
      };

      initSlider() {
        const sliderEl = this.modalContent.querySelector(
          ".product__media-list[data-quick-view-type]"
        );
        if (!sliderEl) return;

        const sliderWrapper = sliderEl.querySelector(
          ".product__media-list-container"
        );
        const slides = sliderEl.querySelectorAll(".product__media-item");
        if (!sliderWrapper || slides.length < 2) return;

        const desktopType = sliderEl.dataset?.desktopType || "slider";
        const mobileType = sliderEl.dataset?.mobileType || "slider";
        const hasDesktopZoom = sliderEl.dataset?.hasDesktopZoom === "true";
        const mediaBox = sliderEl.closest(".product__media");
        const thumbsSlider = mediaBox?.querySelector(".js-media-sublist");

        const quickViewType = sliderEl.dataset.quickViewType;

        sliderWrapper.classList.add("swiper-wrapper");
        slides.forEach((slide) => slide.classList.add("swiper-slide"));

        const buttonPrev = sliderEl.querySelector(".swiper-button-prev");
        const buttonNext = sliderEl.querySelector(".swiper-button-next");

        const initSwiper = () => {
          if (sliderEl.swiper && !sliderEl.swiper.destroyed) {
            return sliderEl.swiper;
          }

          return new Swiper(sliderEl, {
            slidesPerView: 1,
            spaceBetween: 0,
            direction: "horizontal",
            speed: 800,
            autoHeight: true,
            loop: false,
            watchSlideProgress: true,
            allowTouchMove: true,
            mousewheel: {
              forceToAxis: true,
            },
            grabCursor: quickViewType === "drawer" ? true : false,
            navigation: {
              nextEl: buttonNext,
              prevEl: buttonPrev,
              disabledClass: "swiper-button-disabled",
            },
            pagination: {
              el: this.modalContent.querySelector(
                ".product__media-wrapper .swiper-pagination"
              ),
              type: "progressbar",
            },
            breakpoints: {
              990: {
                direction: "vertical",
                allowTouchMove: !hasDesktopZoom,
              },
            },
            thumbs: {
              swiper: thumbsSlider?.swiper ? thumbsSlider.swiper : "",
            },
          });
        };

        const destroySwiper = () => {
          if (sliderEl.swiper) {
            sliderEl.swiper.destroy();
          }
          sliderEl.querySelectorAll(".swiper-wrapper").forEach((wrapper) => {
            wrapper.removeAttribute("style");
          });
          sliderEl.querySelectorAll(".swiper-slide").forEach((slide) => {
            slide.removeAttribute("style");
          });
        };

        const hasDesktopSwiper =
          desktopType === "slider" || desktopType == "slider_previews";
        const hasMobileSwiper =
          mobileType === "slider" || mobileType == "slider_previews";

        if (hasDesktopSwiper && hasMobileSwiper) {
          initSwiper();
        } else {
          this.sliderObserver = new ResizeObserver((entries) => {
            entries.forEach((entry) => {
              if (window.innerWidth >= 990 && hasDesktopSwiper) {
                initSwiper();
                return;
              }

              if (window.innerWidth < 990 && hasMobileSwiper) {
                initSwiper();
                return;
              }

              destroySwiper();
            });
          });

          this.sliderObserver.observe(this.modalContent);
        }
      }

      createFullDetailsButton(opener) {
        const infoContainer = this.modalContent.querySelector(
          ".product__info-container"
        );
        if (!infoContainer || !opener?.dataset?.productUrl) return;

        const moreBtn = document.createElement("a");
        moreBtn.innerHTML = `<span class="hover-underlined-link">${theme.quickviewMore}</span>`;
        moreBtn.setAttribute("href", opener.dataset.productUrl);
        moreBtn.setAttribute(
          "class",
          "product__full-details button button--tertiary focus-inset"
        );

        infoContainer.appendChild(moreBtn);
      }

      clearMediaClasses() {
        const mediaWrapper = this.modalContent.querySelector(".product__media");
        if (!mediaWrapper) return;

        const keepClasses = ["product__media", "product__media--no-media"];
        const filteredClasses = [...mediaWrapper.classList].filter((cssClass) =>
          keepClasses.includes(cssClass)
        );

        // mediaWrapper.className = filteredClasses.join(" ");

        if (this.classList.contains("quick-view-modal--drawer")) {
          mediaWrapper.querySelectorAll("deferred-media").forEach((el) => {
            el.remove();
          });
        }
      }

      reorderBlocks() {
        const infoContainer = this.modalContent.querySelector(
          ".product__info-container"
        );
        if (!infoContainer) return;

        const needOrder = [
          // ".product__text",
          // ".product__title",
          // ".product__price",
          // ".product__sku",
          // ".product__description",
          ".product__media", // product__media reordered only for drawer type
          ".product-icon-with-text",
          ".product__inventory",
          // ".product-parameters",
          ".product-extra-option",
          // ".product__buy-buttons",
        ];

        const multipleBlockSelectors = [
          ".product__text",
          ".product-icon-with-text",
          ".product-extra-option",
        ];

        const orderedElements = [];

        needOrder.forEach((selector) => {
          // skip .product__media for popup type
          if (
            this.classList.contains("quick-view-modal--popup") &&
            selector === ".product__media"
          ) {
            return;
          }

          // for multiple elements (block limit != 1)
          if (multipleBlockSelectors.includes(selector)) {
            const elements = this.modalContent.querySelectorAll(selector);
            elements.forEach((el) => orderedElements.push(el));
            return;
          }

          // for single element
          const element = this.modalContent.querySelector(selector);
          if (element) orderedElements.push(element);
        });

        orderedElements.forEach((el) => {
          infoContainer.appendChild(el);
        });
      }

      updateImageSizesDrawer() {
        const mediaImages = this.modalContent.querySelectorAll(
          ".product__media-img img"
        );
        if (!mediaImages.length) return;

        let mediaImageSizes = "282px"; // image width 94px * 3 DPR
        mediaImages.forEach((img) =>
          img.setAttribute("sizes", mediaImageSizes)
        );
      }

      setFocusElement(opener) {
        const productCard = opener.closest("product-card");
        const productCardLink = productCard?.querySelector(
          ".product-card__link-overlay"
        );
        if (productCardLink) this.openedBy = productCardLink;
      }
    }
  );
}
